/************************************** THIRD-PARTY MODULES ***************************************/
import localforage from 'localforage';
import moment from 'moment';
import {isComputedProp, runInAction} from 'mobx';

/**************************************** PROJECT MODULES *****************************************/
import {setDeserializer, getSerializer, getDeserializer, isMomentProp} from './serialization-utils';

/********************************************* EXPORT *********************************************/
/**
 * Serialize data for storage (usually no serialization necessary)
 * @return {any} Data converted into a storable form. Usually no change.
 */
export function prepDataForStorage(instance: object, instanceKey: string, value: any) {
    // Run serializer on data if one is present
    const serializer = getSerializer(instance, instanceKey);
    if (serializer) return serializer(value);

    // Handle moment strings (for cases where no serializer set up)
    if (moment.isMoment(value)) {
        setDeserializer(instance, instanceKey, (jsonStr: string) => moment(jsonStr));
        return value.toJSON();
    }

    // Return value as-is if no serializer present, and not a moment object
    return value;
}

/**
 * If data present in store at dbStorageKey, load it into memory (set instance[key]);
 * If not, store the default value for the property in the store at dbStorageKey.
 *
 * @param {object} instance Instance of a class.
 * @param {string} dbStorageKey Key data is stored at in the db
 * @param {string} key Key on the model object at which the data to be stored can be found
 */
export async function initLocalforageKey(
    instance: object,
    instanceKey: string,
    dbStorageKey: string
): Promise<void> {
    // Grab value from localForage
    const storedValue = await localforage.getItem(dbStorageKey);

    /*
     *    STORE -> INSTANCE
     */

    // Copy value from localForage -> instance (if found in localForage)
    if (storedValue && !isComputedProp(instance, instanceKey)) {
        runInAction(`initLocalforageKey::set ${instanceKey}`, () => {
            instance[instanceKey] = rehydratePropFromStoreValue(instance, instanceKey, storedValue);
        });

        /*
     *    INSTANCE -> STORE
     */

        // Copy value from instance -> localForage if found in instance (& not found in localForage)
    } else if (!storedValue) {
        try {
            // Store in localForage (IndexedDb), clean value before storing
            const itemSet = await localforage.setItem<string>(
                dbStorageKey,
                prepDataForStorage(instance, instanceKey, instance[instanceKey])
            );
        } catch (err) {
            console.error(`initLocalforageKey: await localforage.setItem/prepDataForStorage err:`, err);
        }
    }
}

/**
 * Deserialize given data stored for given instance at given key.
 *
 * @param {object} instance Instance of a data class.
 * @param {string} instanceKey Key of data class to deserialize data for
 * @param {any} storedValue Data retrieved from local storage (IndexedDB)
 *
 * @return {any} Property value
 */
function rehydratePropFromStoreValue<RetType = any>(
    instance: object,
    instanceKey: string,
    storedValue: any
): RetType {
    return getDeserializer(instance, instanceKey) || isMomentProp(instance, instanceKey)
        ? moment(storedValue)
        : storedValue;
}
